// src/components/PursuitAccess/ExportManagerAssessmentsButton.jsx
import React, { useState } from "react";
import { Button, Notification } from "grommet";

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
        : "";

// Build a unique-user list per assessment, collapsing multiple entries per user.
// If a user has any completed entry, mark them as completed.
const getUniqueUsers = (entries = []) => {
    const byKey = new Map(); // key by user_id if present else by email
    for (const e of entries) {
        const key = e.user_id ?? e.user_email ?? `${e.user_name}|${e.user_email}`;
        const existing = byKey.get(key);
        const completed = e.is_complete === 1;
        if (!existing) {
            byKey.set(key, {
                name: e.user_name || e.user_email || "",
                email: e.user_email || "",
                country: e.user_country || "",
                completed,
            });
        } else {
            existing.completed = existing.completed || completed;
        }
    }
    return Array.from(byKey.values());
};

const toSummaryRows = (groups = []) =>
    groups.map((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        const first = entries[0] || {};

        const uniqueUsers = getUniqueUsers(entries);
        const completedUsers = uniqueUsers.filter((u) => u.completed);
        const notCompletedUsers = uniqueUsers.filter((u) => !u.completed);

        const topics = Array.from(
            new Set(
                entries
                    .flatMap((e) => [
                        e.topic1_name,
                        e.topic2_name,
                        e.topic3_name,
                        e.topic4_name,
                        e.topic5_name,
                    ])
                    .filter(Boolean)
            )
        );

        return {
            // No "Assessment ID" as requested
            Assessment: meta.assessment_name || "N/A",
            "Created Date": fmtDate(meta.creation_date),
            "Created Time": meta.creation_time || "",

            "Manager Name": first.manager_name || "",
            "Manager Email": first.manager_email || "",
            "Manager Country": first.manager_country || "",

            "Users Selected (Count)": uniqueUsers.length,
            "Completed (Count)": completedUsers.length,
            "Not Completed (Count)": notCompletedUsers.length,

            "Users Selected (Names)": uniqueUsers.map((u) => u.name).join("; "),
            "Users Selected (Emails)": uniqueUsers.map((u) => u.email).join("; "),

            "Completed Users (Emails)": completedUsers.map((u) => u.email).join("; "),
            "Not Completed Users (Emails)": notCompletedUsers
                .map((u) => u.email)
                .join("; "),

            Topics: topics.join("; "),
        };
    });

const toUsersRows = (groups = []) =>
    groups.flatMap((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        const users = getUniqueUsers(entries);
        return users.map((u) => ({
            Assessment: meta.assessment_name || "N/A", // no id
            "User Name": u.name,
            "User Email": u.email,
            "User Country": u.country,
            Completed: u.completed ? "Yes" : "No",
        }));
    });

const toEntryRows = (groups = []) =>
    groups.flatMap((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        return entries.map((e) => ({
            Assessment: meta.assessment_name || "N/A", // no id
            "User Name": e.user_name || e.user_email || "",
            "User Email": e.user_email || "",
            "User Country": e.user_country || "",
            "Start Date": fmtDate(e.start_date),
            "Start Time": e.start_time || "",
            "End Date": fmtDate(e.end_date),
            "End Time": e.end_time || "",
            Questions: e.query_count ?? "",
            Correct: e.correct_count ?? "",
            Incorrect: e.incorrect_count ?? "",
            "Topic 1": e.topic1_name || "",
            "Topic 2": e.topic2_name || "",
            "Topic 3": e.topic3_name || "",
            "Topic 4": e.topic4_name || "",
            "Topic 5": e.topic5_name || "",
            Completed: e.is_complete === 1 ? "Yes" : "No",
        }));
    });

const rowsToCSV = (rows) => {
    const headers = Object.keys(rows[0] || {});
    const esc = (v) => {
        const s = (v ?? "").toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))];
    return lines.join("\n");
};

const downloadBlob = (filename, mime, content) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const ExportManagerAssessmentsButton = ({ groups = [], onDone }) => {
    const [toast, setToast] = useState(null);
    const handleExport = async () => {
        setToast({
            status: "normal",
            title: "Export in progress",
            message: "Report is downloading and may take some time.",
        });
        const summary = toSummaryRows(groups);
        const users = toUsersRows(groups);
        const entries = toEntryRows(groups);

        const stamp = new Date().toISOString().slice(0, 10);
        const baseName = `HPE Digital Skill Advisor - Manager created assessments - ${stamp}`;

        try {
            const XLSX = await import("xlsx");
            const wb = XLSX.utils.book_new();

            const wsSummary = XLSX.utils.json_to_sheet(summary.length ? summary : [{ Info: "No data" }]);
            XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

            const wsUsers = XLSX.utils.json_to_sheet(users.length ? users : [{ Info: "No users" }]);
            XLSX.utils.book_append_sheet(wb, wsUsers, "Users");

            if (entries.length) {
                const wsEntries = XLSX.utils.json_to_sheet(entries);
                XLSX.utils.book_append_sheet(wb, wsEntries, "Entries");
            }

            XLSX.writeFile(wb, `${baseName}.xlsx`);
            const doneMsg = `Exported ${summary.length} assessment(s), ${users.length} unique user(s)${entries.length ? ` and ${entries.length} entry rows` : ""
                }.`;

            setToast({
                status: "normal",
                title: "Export complete",
                message: doneMsg,
            });
            onDone?.(doneMsg);
        } catch (e) {
            console.warn("[Export] XLSX failed, falling back to CSV:", e);
            // Fallback CSV: write Summary sheet only
            const csv = summary.length ? rowsToCSV(summary) : "No data";
            downloadBlob(`${baseName}.csv`, "text/csv;charset=utf-8", csv);
            const doneMsg = "Export complete (CSV fallback).";
            setToast({
                status: "normal",
                title: "Export complete",
                message: doneMsg,
            });
            onDone?.(doneMsg);
        }
    };

    return (
        <>
            <Button
                label="Export"
                onClick={handleExport}
                secondary
                disabled={!groups.length}
            />

            {toast && (
                <Notification
                    toast
                    status={toast.status}
                    title={toast.title}
                    message={toast.message}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
};

export default ExportManagerAssessmentsButton;
